import {
    ConversationAssistantResponseFragmentDoc,
    SubmittedCaseResponseFragmentDoc,
    SummarizeResponseFragmentDoc,
} from '@graphql/generated/graphql';

import { isArrayEmpty } from '@utils/helpers';

import { QueryResult } from '@apollo/client';
import { client } from '@boot/apolloClient';
import { currentLanguageVar } from '@boot/reactiveVariables';

interface BasePaginationList {
    list: {
        edges: {
            // TODO: REPAIR ANY TYPE
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            cursor: any;
            // TODO: REPAIR ANY TYPE
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            node: any;
        }[];
        pageInfo: {
            endCursor?: string | null | undefined;
            hasNextPage: boolean;
            hasPreviousPage: boolean;
            pageSize: number;
            startCursor?: string | null | undefined;
        };
    };
}

interface BasePaginationListVariables {
    input?: {
        paging?: {
            first?: number | null;
            last?: number | null;
        } | null;
    } | null;
}

// TODO: add this hook to @speeki/react-hooks
/**
 * Use only with PageInfoV2.
 * @param result from useQuery/useLazyQuery. You have to provide `data` with `list`.
 * @param variables passed for useQuery/useLazyQuery. You have to provide `first` and `last` in paging object!
 * @description Any Query that you this pagination should be rewritten
 * @returns query result with infinite pagination variables.
 * @deprecated
 */
export const useQueryInfinitePagination = <
    T extends QueryResult<BasePaginationList, BasePaginationListVariables>,
>(
    result: T,
    variables: T['variables'],
) => {
    const { data: resultData, fetchMore, loading } = result;

    const first = variables?.input?.paging?.first;
    const last = variables?.input?.paging?.last;

    // TODO: find way to validate that with typescript
    if (!first || !last)
        throw new Error(
            'useQueryInfinitePagination :: "first" or/and "last" args not found',
        );

    const data = resultData?.list;
    const pageInfo = data?.pageInfo;

    const nodes = data?.edges.map((edge) => edge.node) || [];
    const after = pageInfo?.endCursor;
    const before = pageInfo?.startCursor;
    const hasNextPage = pageInfo?.hasNextPage || false;

    const handleFetchAfter = () => {
        if (loading || !after || !hasNextPage) return;

        void fetchMore({
            variables: {
                ...variables,
                input: {
                    ...variables?.input,
                    paging: {
                        after,
                        first,
                    },
                },
            },
        });
    };

    const handleFetchBefore = () => {
        if (loading || !before) return;

        void fetchMore({
            variables: {
                ...variables,
                input: {
                    ...variables?.input,
                    paging: {
                        before,
                        last,
                    },
                },
            },
        });
    };

    return {
        ...result,
        handleFetchAfter,
        handleFetchBefore,
        hasNextPage,
        isEmptyList: isArrayEmpty(nodes),
        nodes,
    };
};

export const updateUnreadNotificationCount = (amount: number | 'CLEAR') =>
    client.cache.modify({
        fields: {
            getUnreadNotificationCount(value: number) {
                return amount === 'CLEAR' ? 0 : value - amount;
            },
        },
    });

export const summarizeResponse = (caseUuid: string) => {
    const currentLanguage = currentLanguageVar();
    return client.readFragment(
        {
            id: `CaseReportSummaryResponse:${caseUuid}`,
            fragment: SummarizeResponseFragmentDoc,
            variables: {
                targetLanguage: currentLanguage,
            },
        },
        true,
    );
};

export const submittedCaseResponse = (caseId: string) =>
    client.readFragment({
        id: `CaseReportSubmit:${caseId}`,
        fragment: SubmittedCaseResponseFragmentDoc,
    });

/**
 * Drops every `ROOT_MUTATION` field from the cache. Mutation results are
 * written there under field keys that serialise the raw variables - for the
 * report flow that means the reporter's email address (submit and both
 * verification mutations) and the case PIN. Apollo itself removes those
 * fields once each mutation completes (`keepRootFields` defaults to false),
 * so this is a belt-and-braces guarantee at the flow's security boundary
 * rather than a fix: the submitted page must not depend on a default set
 * elsewhere. The normalised entities the page still needs (the case's
 * uuid/caseId) live under their own ids and are untouched - `cache.gc()` is
 * deliberately not called, since it would collect them.
 */
export const evictMutationRoot = () =>
    client.cache.evict({ id: 'ROOT_MUTATION' });

export const assistantResponse = (caseId: string) => {
    const currentLanguage = currentLanguageVar();
    return client.readFragment({
        id: `ConversationAssistantResponse:${caseId}`,
        fragment: ConversationAssistantResponseFragmentDoc,
        variables: {
            targetLanguage: currentLanguage,
        },
    });
};

export const isValidStringDate = (value: string) => {
    const date = new Date(value);

    if (isNaN(date.getTime())) return false;

    return true;
};
